import React, { useState, useEffect, useCallback } from 'react';
import { io } from 'socket.io-client';
import './App.css';
import { Power, VolumeX, Volume2, ChevronUp, ChevronDown, ChevronLeft, ChevronRight, Home, ArrowLeft, Monitor, Search, Tv, PlayCircle, PauseCircle, StopCircle, Rewind, FastForward, Settings, Info, List } from 'lucide-react';
import TVDiscovery from './components/TVDiscovery';
import { useAuth0 } from '@auth0/auth0-react';
import LoginButton from './LoginButton';
import LogoutButton from './LogoutButton';
import Profile from './Profile';

const API_BASE_URL = (process.env.REACT_APP_SERVER_URL || '').replace(/\/$/, '');
const SOCKET_URL = API_BASE_URL || undefined;

function App() {
  //  // TEMPORARY TESTING OVERRIDE - BYPASS AUTH0
  // const forceBypassAuth = true;
  // const { isAuthenticated, isLoading, error, user, getAccessTokenSilently } = forceBypassAuth ? {
  // isAuthenticated: true,
  // isLoading: false,
  // error: null,
  // user: { name: "Test User", picture: "" },
  // getAccessTokenSilently: async () => ""
  // } : useAuth0();
  const { isAuthenticated, isLoading, error, user, getAccessTokenSilently } = useAuth0();

  const [socket, setSocket] = useState(null);
  const [connected, setConnected] = useState(false);
  const [tvStatus, setTvStatus] = useState('Unknown');
  const [message, setMessage] = useState('');
  const [showDiscovery, setShowDiscovery] = useState(false);
  const [tvConfig, setTvConfig] = useState(null);
  const [userClaims, setUserClaims] = useState(null);

  const [tvInfo, setTvInfo] = useState({
    isOn: false,
    volume: 0,
    muted: false,
    currentApp: null,
    currentChannel: null,
    availableApps: [],
    availableInputs: [],
    availableChannels: []
  });
  const [latestAvailableInputs, setLatestAvailableInputs] = useState([]);
  const [showAdvancedControls, setShowAdvancedControls] = useState(false);
  
  // Fetch and log JWT token when authenticated
  useEffect(() => {
    const getToken = async () => {
      if (isAuthenticated) {
        try {
          const token = await getAccessTokenSilently();
          console.log('Access Token:', token);
          console.log('Token length:', token.length);
          
          // Check if token is in JWT format (has 3 parts separated by dots)
          const parts = token.split('.');
          console.log('Token parts count:', parts.length);
          
          if (parts.length === 3) {
            // It's a JWT token - decode it
            const base64Url = parts[1];
            const base64 = base64Url.replace(/-/g, '+').replace(/_/g, '/');
            const jsonPayload = decodeURIComponent(
              atob(base64)
                .split('')
                .map((c) => '%' + ('00' + c.charCodeAt(0).toString(16)).slice(-2))
                .join('')
            );
            const decodedToken = JSON.parse(jsonPayload);
            console.log('✅ JWT Token - Decoded Payload:', decodedToken);
            console.log('Token expires at:', new Date(decodedToken.exp * 1000));
            
            // Decode the JWT to get custom claims
            if (user) {
              console.log('User claims:', user);
              setUserClaims({
                deviceId: user['controller_device_id'],
                email: user.email,
                userId: user.sub
              });
            }
          } else {
            console.warn('⚠️ This is an OPAQUE token, not a JWT.');
            console.warn('To get a proper JWT token:');
            console.warn('1. Create a custom API in your Auth0 dashboard');
            console.warn('2. Use that API identifier as the audience');
            console.warn('3. Update REACT_APP_AUTH0_AUDIENCE in .env file');
          }
        } catch (error) {
          console.error('Error getting access token:', error);
          console.error('This might mean the audience is not properly configured in Auth0');
        }
      }
    };
    
    getToken();
  }, [isAuthenticated, getAccessTokenSilently, user]);
  
  useEffect(() => {
    console.log("______",SOCKET_URL)

    // Connect to Socket.IO server with fallbacks for different environments
    let newSocket;
    try {
      // Try direct connection first
      newSocket = io(SOCKET_URL || undefined, {
        path: '/socket.io',
        transports: ['websocket', 'polling'],
        reconnectionAttempts: 10,
        reconnectionDelay: 2000,
        reconnectionDelayMax: 10000,
        timeout: 45000, // Match server's connectTimeout
        autoConnect: true
      });
      
      console.log('Connecting to Socket.IO server...');
      console.log("______",SOCKET_URL)

    } catch (err) {
      console.error('Socket.IO connection error:', err);
   
      // Fallback to direct connection
      newSocket = io(SOCKET_URL || 'https://tvconnect.guycon.com.au');
    }
    
    newSocket.on('connect', () => {
      setConnected(true);
      setMessage('Connected to server');

      // Identify as frontend client so server knows this socket type
      try {
        const identifyData = { clientType: 'frontend' };
        
        // Include user info if authenticated
        if (isAuthenticated && user) {
          identifyData.userId = user.sub; // Auth0 user ID
          identifyData.userEmail = user.email;
          identifyData.deviceId = user['controller_device_id']; // From Auth0 custom claim
        }
        
        console.log('Identifying with:', identifyData);
        newSocket.emit('identify', identifyData);
      } catch (identifyError) {
        console.error('Error sending identify event:', identifyError);
      }
    });
    
    newSocket.on('disconnect', (reason) => {
      setConnected(false);
      setMessage('Disconnected from server');
      console.log('❌ Disconnected from server. Reason:', reason);
    });
    
    newSocket.on('connect_error', (error) => {
      console.error('❌ Connection error:', error.message);
      setMessage(`Connection error: ${error.message}`);
    });
    
    newSocket.on('reconnect_attempt', (attemptNumber) => {
      console.log(`🔄 Reconnection attempt ${attemptNumber}...`);
    });
    
    newSocket.on('reconnect_failed', () => {
      console.error('❌ Reconnection failed after all attempts');
      setMessage('Failed to reconnect to server');
    });
    
    newSocket.on('tv_status', (status) => {
      setTvStatus(status);
    });
    
    // Handle full TV status updates
    newSocket.on('tv_status_update', (data) => {
      console.log('Received TV status update:', data);
      if (data && typeof data === 'object') {
        console.log('Received TV status update:', data);
        // If it's the legacy format with just status property
        if (data.status && Object.keys(data).length === 1) {
          setTvStatus(data.status);
        } else {
          // Process full TV status object
          setTvInfo({
            isOn: data.is_on || false,
            volume: data.volume || 0,
            muted: data.muted || false,
            currentApp: data.current_app || null,
            currentChannel: data.current_channel || null,
            availableApps: data.apps || data.available_apps || [],
            availableInputs: data.available_inputs || latestAvailableInputs,
            availableChannels: data.available_channels || []
          });

          if (Array.isArray(data.available_inputs)) {
            setLatestAvailableInputs(data.available_inputs);
          }
          // Also update the simple status for compatibility
          if (data.is_on) {
            setTvStatus('Connected');
          } else {
            setTvStatus('Disconnected');
          }
        }
      }
    });
    
    // Handle command results
    newSocket.on('command_result', (result) => {
      console.log('Command result received:', result);
      
      if (result.success) {
        setMessage(`Command ${result.command} executed successfully`);
        
        // For get_status, we want to handle it regardless of result structure
        if (result.command === 'get_status') {
          // Handle get_status command specifically
          // Update TV info
          // Check both possible locations for status data
          const data = result.params || (result.result && result.result.status) || {};
          console.log('Received TV status:', data);
          setTvInfo({
            isOn: data.is_on || false,
            volume: data.volume || 0,
            muted: data.muted || false,
            currentApp: data.current_app || null,
            currentChannel: data.current_channel || null,
            availableApps: data.apps || data.available_apps || [],
            availableInputs: data.available_inputs || latestAvailableInputs,
            availableChannels: data.available_channels || []
          });
          if (Array.isArray(data.available_inputs)) {
            setLatestAvailableInputs(data.available_inputs);
          }
        }
      } else {
        setMessage(`Error: ${result.error || 'Unknown error'}`);
      }
    });
    
    newSocket.on('message', (msg) => {
      setMessage(msg);
    });

    setSocket(newSocket);

    return () => {
      newSocket.disconnect();
    };
  }, []); // Empty dependency array - only connect once on mount

  // Re-identify when user authentication status changes
  useEffect(() => {
    if (socket && connected && isAuthenticated && user) {
      console.log('User authenticated, re-identifying with user info...');
      const identifyData = {
        clientType: 'frontend',
        userId: user.sub,
        userEmail: user.email,
        deviceId: user['controller_device_id'] // From Auth0 custom claim
      };
      console.log('Re-identifying with:', identifyData);
      socket.emit('identify', identifyData);
    }
  }, [socket, connected, isAuthenticated, user]);

  const sendCommand = useCallback((command, params = {}) => {
    if (socket && connected) {
      const commandData = { command, params };
      
      // Include user identity if authenticated
      if (isAuthenticated && user) {
        commandData.userId = user.sub;
        commandData.deviceId = user['controller_device_id'];
      }
      
      socket.emit('tv_command', commandData);
      setMessage(`Sent command: ${command}`);
    } else {
      setMessage('Not connected to server');
    }
  }, [socket, connected, isAuthenticated, user]);

  useEffect(() => {
    if (!socket || !connected) {
      return;
    }

    const intervalId = setInterval(() => {
      sendCommand('get_status');
    }, 5000);

    return () => clearInterval(intervalId);
  }, [socket, connected, sendCommand]);
  
  // Fetch TV status when component mounts and connection is established
  useEffect(() => {
    if (socket && connected) {
      // Request TV status
      sendCommand('get_status');
    }
  }, [connected, socket, sendCommand]);

  // Handle TV selection from discovery component
  const handleTVSelected = (tv) => {
    setTvConfig(tv);
    setShowDiscovery(false);
    setMessage(`Connected to TV at ${tv.ip}`);
  };

  // Auth0 loading state
  if (isLoading) {
    return (
      <div className="app-container">
        <div className="loading-state">
          <div className="loading-text">Loading...</div>
        </div>
      </div>
    );
  }

  // Auth0 error state
  if (error) {
    return (
      <div className="app-container">
        <div className="error-state">
          <div className="error-title">Oops!</div>
          <div className="error-message">Something went wrong</div>
          <div className="error-sub-message">{error.message}</div>
        </div>
      </div>
    );
  }

  // Not authenticated - show login screen
  if (!isAuthenticated) {
    return (
      <div className="app-container">
        <div className="main-card-wrapper">
          <img 
            src="https://cdn.auth0.com/quantum-assets/dist/latest/logos/auth0/auth0-lockup-en-ondark.png" 
            alt="Auth0 Logo" 
            className="auth0-logo"
            onError={(e) => {
              e.currentTarget.style.display = 'none';
            }}
          />
          <h1 className="main-title">TV Control 2025</h1>
          <div className="action-card">
            <p className="action-text">Please sign in to access TV controls</p>
            <LoginButton />
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="container">
      <header className="app-header">
        <h1 className="app-title">TV Control 2025</h1>
        <p className="app-subtitle">Simple TV controls for WebOS TV</p>
        
        {/* Auth0 User Profile Section */}
        <div className="auth-profile-section">
          <div className="profile-compact">
            {user && (
              <>
         
                <img 
                  src={user.picture || `data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='40' height='40' viewBox='0 0 110 110'%3E%3Ccircle cx='55' cy='55' r='55' fill='%2363b3ed'/%3E%3Cpath d='M55 50c8.28 0 15-6.72 15-15s-6.72-15-15-15-15 6.72-15 15 6.72 15 15 15zm0 7.5c-10 0-30 5.02-30 15v3.75c0 2.07 1.68 3.75 3.75 3.75h52.5c2.07 0 3.75-1.68 3.75-3.75V72.5c0-9.98-20-15-30-15z' fill='%23fff'/%3E%3C/svg%3E`}
                  alt={user.name || 'User'}
                  style={{ width: '40px', height: '40px', borderRadius: '50%', marginRight: '10px' }}
                />
                <span style={{ color: '#fff', marginRight: '15px' }}>{user.name}</span>
              </>
            )}
            <LogoutButton />
          </div>
        </div>
        
        <div className="status-bar">
          <div className="status-item">
            <span className={`status-indicator ${connected ? 'status-connected' : 'status-disconnected'}`}></span>
            <span>{connected ? 'Connected to server' : 'Not connected to server'}</span>
          </div>
          
          <div className="status-item">
            <span className={`status-indicator ${tvStatus === 'Connected' ? 'status-connected' : tvStatus === 'Disconnected' ? 'status-disconnected' : 'status-unknown'}`}></span>
            <span>TV: {tvStatus}</span>
          </div>
        </div>
        
        {/* TV discovery button */}
        <div className="discovery-container">
          <button 
            className="discovery-button"
            onClick={() => setShowDiscovery(!showDiscovery)}
          >
            <Tv size={24} />
            {tvConfig ? 'Change TV' : 'Find TV'}
          </button>
          {tvConfig && (
            <div className="tv-info">
              Connected to: <span style={{fontWeight: 'bold'}}>{tvConfig.ip}</span>
            </div>
          )}
        </div>
      </header>
      
      {/* TV Discovery Component */}
      {showDiscovery && (
        <TVDiscovery onTVSelected={handleTVSelected} />
      )}
      
      <div className="card-container">
        <h2 className="card-title">
          <Power size={24} />
          Power
        </h2>
        <div className="button-row">
          <button 
            className="control-button power-button"
            onClick={() => sendCommand('power')}
          >
            <Power size={48} />
            <span className="button-label">Power</span>
          </button>
        </div>
      </div>
      
      <div className="card-container">
        <h2 className="card-title">
          <Volume2 size={24} />
          Volume
        </h2>
        <div className="buttons-container">
          <button 
            className="control-button volume-button"
            onClick={() => sendCommand('volume_up')}
          >
            <Volume2 size={48} />
            <span className="button-label">Up</span>
          </button>
          <button 
            className="control-button volume-button"
            onClick={() => sendCommand('volume_down')}
          >
            <Volume2 size={48} />
            <span className="button-label">Down</span>
          </button>
          <button 
            className="control-button volume-button"
            onClick={() => sendCommand('mute')}
          >
            <VolumeX size={48} />
            <span className="button-label">Mute</span>
          </button>
        </div>
      </div>
      
      <div className="card-container">
        <h2 className="card-title">
          <Tv size={24} />
          Channel
        </h2>
        <div className="buttons-container">
          <button 
            className="control-button channel-button"
            onClick={() => sendCommand('channel_up')}
          >
            <ChevronUp size={48} />
            <span className="button-label">Up</span>
          </button>
          <button 
            className="control-button channel-button"
            onClick={() => sendCommand('channel_down')}
          >
            <ChevronDown size={48} />
            <span className="button-label">Down</span>
          </button>
        </div>
      </div>
      
      <div className="card-container">
        <h2 className="card-title">
          <ArrowLeft size={24} />
          Navigation
        </h2>
        <div className="nav-grid">
          <div></div>
          <button 
            className="control-button navigation-button"
            onClick={() => sendCommand('up')}
          >
            <ChevronUp size={48} />
          </button>
          <div></div>
          
          <button 
            className="control-button navigation-button"
            onClick={() => sendCommand('left')}
          >
            <ChevronLeft size={48} />
          </button>
          <button 
            className="control-button navigation-button center-button"
            onClick={() => sendCommand('ok')}
          >
            <span className="button-label">OK</span>
          </button>
          <button 
            className="control-button navigation-button"
            onClick={() => sendCommand('right')}
          >
            <ChevronRight size={48} />
          </button>
          
          <div></div>
          <button 
            className="control-button navigation-button"
            onClick={() => sendCommand('down')}
          >
            <ChevronDown size={48} />
          </button>
          <div></div>
        </div>
        
        <div className="buttons-container">
          <button 
            className="control-button navigation-button"
            onClick={() => sendCommand('home')}
          >
            <Home size={48} />
            <span className="button-label">Home</span>
          </button>
          <button 
            className="control-button navigation-button"
            onClick={() => sendCommand('back')}
          >
            <ArrowLeft size={48} />
            <span className="button-label">Back</span>
          </button>
        </div>
      </div>
      
      <div className="card-container">
        <h2 className="card-title">
          <Monitor size={24} />
          Input Source
        </h2>
        <div className="buttons-container">
          {(tvInfo.availableInputs && tvInfo.availableInputs.length > 0)
            ? tvInfo.availableInputs.map((input) => (
              <button
                key={input.id || input.label}
                className="control-button input-button"
                onClick={() => sendCommand('set_input_source', { sourceId: input.id, label: input.label })}
              >
                <Monitor size={48} />
                <span className="button-label">{input.label || input.id || 'Input'}</span>
              </button>
            ))
            : (
              <>
                <button 
                  className="control-button input-button"
                  onClick={() => sendCommand('input_hdmi1')}
                >
                  <Monitor size={48} />
                  <span className="button-label">HDMI 1</span>
                </button>
                <button 
                  className="control-button input-button"
                  onClick={() => sendCommand('input_hdmi2')}
                >
                  <Monitor size={48} />
                  <span className="button-label">HDMI 2</span>
                </button>
                <button 
                  className="control-button input-button"
                  onClick={() => sendCommand('input_tv')}
                >
                  <Tv size={48} />
                  <span className="button-label">TV</span>
                </button>
              </>
            )}
        </div>
      </div>

      <div className="card-container">
        <div className="advanced-controls-header">
          <h2 className="card-title mb-0">
            <Settings size={24} />
            Advanced Controls
          </h2>
          <button
            onClick={() => setShowAdvancedControls(!showAdvancedControls)}
            className="advanced-toggle"
          >
            {showAdvancedControls ? 'Hide' : 'Show'}
          </button>
        </div>
        
        {showAdvancedControls && (
          <>
            {/* Media playback controls */}
            <div className="mb-8">
              <h3 className="section-title">
                <PlayCircle size={20} className="section-icon" />
                Media Controls
              </h3>
              <div className="buttons-container">
                <button 
                  className="control-button media-button"
                  onClick={() => sendCommand('play')}
                >
                  <PlayCircle size={48} />
                  <span className="button-label">Play</span>
                </button>
                <button 
                  className="control-button media-button"
                  onClick={() => sendCommand('pause')}
                >
                  <PauseCircle size={48} />
                  <span className="button-label">Pause</span>
                </button>
                <button 
                  className="control-button media-button"
                  onClick={() => sendCommand('stop')}
                >
                  <StopCircle size={48} />
                  <span className="button-label">Stop</span>
                </button>
                <button 
                  className="control-button media-button"
                  onClick={() => sendCommand('rewind')}
                >
                  <Rewind size={48} />
                  <span className="button-label">Rewind</span>
                </button>
                <button 
                  className="control-button media-button"
                  onClick={() => sendCommand('fast_forward')}
                >
                  <FastForward size={48} />
                  <span className="button-label">Fast Forward</span>
                </button>
              </div>
            </div>
          
            {/* Volume slider */}
            <div className="mb-8">
              <h3 className="section-title">
                <Volume2 size={20} className="section-icon" />
                Volume Control
              </h3>
              <div className="slider-container">
                <Volume2 size={24} className="text-blue-500" />
                <input
                  type="range"
                  min="0"
                  max="100"
                  value={tvInfo.volume}
                  onChange={(e) => sendCommand('set_volume', { volume: parseInt(e.target.value) })}
                  className="w-64"
                />
                <div className="text-lg font-semibold min-w-[40px] text-center bg-blue-50 px-2 py-1 rounded-md">
                  {tvInfo.volume}
                </div>
              </div>
            </div>
            
            {/* Input source dropdown */}
            {tvInfo.availableInputs.length > 0 && (
              <div className="mb-8">
                <h3 className="section-title">
                  <Monitor size={20} className="section-icon" />
                  Input Source
                </h3>
                <div className="select-container">
                  <select 
                    onChange={(e) => sendCommand('set_source', { source_id: e.target.value })}
                  >
                    <option value="">Select Input</option>
                    {tvInfo.availableInputs.map((input) => (
                      <option key={input.id} value={input.id}>
                        {input.label}
                      </option>
                    ))}
                  </select>
                  <button 
                    className="refresh-button"
                    onClick={() => sendCommand('list_sources')}
                  >
                    <Search size={18} className="mr-2" />
                    Refresh
                  </button>
                </div>
              </div>
            )}
            
            {/* App launcher */}
            {tvInfo.availableApps.length > 0 && (
              <div className="mb-8">
                <h3 className="section-title">
                  <List size={20} className="section-icon" />
                  Launch App
                </h3>
                <div className="select-container">
                  <select 
                    onChange={(e) => e.target.value && sendCommand('launch_app', { app_id: e.target.value })}
                  >
                    <option value="">Select App</option>
                    {tvInfo.availableApps.map((app) => (
                      <option key={app.id} value={app.id}>
                        {app.title}
                      </option>
                    ))}
                  </select>
                  <button 
                    className="refresh-button"
                    onClick={() => sendCommand('list_apps')}
                  >
                    <Search size={18} className="mr-2" />
                    Refresh
                  </button>
                </div>
              </div>
            )}

            {/* TV Status */}
            <div className="status-panel">
              <h3 className="section-title">
                <Info size={20} className="section-icon" />
                TV Status
              </h3>
              <div className="status-grid">
                <div className="status-item">
                  <span className="status-label">Volume:</span>
                  <span className="status-value">{tvInfo.volume}</span>
                </div>
                <div className="status-item">
                  <span className="status-label">Muted:</span>
                  <span className="status-value">{tvInfo.muted ? 'Yes' : 'No'}</span>
                </div>
                <div className="status-item">
                  <span className="status-label">Current App:</span>
                  <span className="status-value">{tvInfo.currentApp || 'Unknown'}</span>
                </div>
                <div className="status-item">
                  <span className="status-label">Power:</span>
                  <span className="status-value">{tvInfo.isOn ? 'On' : 'Off'}</span>
                </div>
              </div>
              <button 
                className="status-refresh"
                onClick={() => sendCommand('get_status')}
              >
                <Info size={18} />
                <span>Refresh Status</span>
              </button>
            </div>
          </>
        )}
      </div>
      
      <div className="message-bar">
        <p className="message-text">{message}</p>
      </div>
    </div>
  );
}

export default App;
